import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { clearToken, getIsPortalAdmin } from '../utils/storage';
import {
    LayoutDashboard,
    Users,
    ShieldHalf,
    Puzzle,
    LogOut,
    Layers,
    Building2,
    Menu,
    X,
    MailPlus,
    Key,
    UsersRound,
    Share2,
    FileText,
    Smartphone,
    Webhook,
    Fingerprint,
} from 'lucide-react';
import { useApps } from '../contexts/AppsContext';

// portalAdminOnly items are install-wide/cross-app (aggregate dashboard) and match
// the backend portalAdminRole gate — hidden from app admins. Apps stays visible: app
// admins manage their OWN app(s) there (the list is UAM-scoped, global actions gated).
const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, portalAdminOnly: true },
    { to: '/apps', label: 'Apps', icon: Layers },
    { to: '/users', label: 'Users', icon: Users },
    { to: '/roles', label: 'Roles', icon: ShieldHalf },
    { to: '/components', label: 'Components', icon: Puzzle },
    { to: '/tenants', label: 'Tenants', icon: Building2 },
    { to: '/invitations', label: 'Invitations', icon: MailPlus },
    { to: '/api-keys', label: 'API Keys', icon: Key },
    { to: '/groups', label: 'Groups', icon: UsersRound },
    { to: '/delegations', label: 'Delegations', icon: Share2 },
    { to: '/audit-trail', label: 'Audit Trail', icon: FileText },
    { to: '/totp-setup', label: 'TOTP Setup', icon: Smartphone },
    { to: '/webhooks', label: 'Webhooks', icon: Webhook },
    { to: '/sso', label: 'SSO', icon: Fingerprint },
];

const ShellLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { ensureAppsLoaded } = useApps();
    const hasEnsuredRef = useRef(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isPortalAdmin = getIsPortalAdmin();
    const visibleNavItems = navItems.filter((item) => !item.portalAdminOnly || isPortalAdmin);

    // Ensure apps are loaded when entering protected routes (e.g., after login)
    // Only run once when ShellLayout first mounts
    useEffect(() => {
        if (!hasEnsuredRef.current) {
            hasEnsuredRef.current = true;
            ensureAppsLoaded();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close sidebar when route changes on mobile
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        clearToken();
        navigate('/login', { replace: true });
    };

    return (
        <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-white to-slate-100">
            {/* Mobile menu button */}
            <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
                <Menu className="h-5 w-5" />
            </button>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Floating Sidebar */}
            <aside
                className={`fixed md:relative z-50 md:z-auto w-64 h-full bg-white/95 backdrop-blur-lg border-r border-slate-200 flex-col shadow-xl md:shadow-sm transition-transform duration-300 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                }`}
            >
                <div className="px-5 py-6 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white grid place-content-center shadow-sm">
                            <Layers className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="text-base font-semibold text-slate-900">User Management</div>
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden p-1 rounded-lg text-slate-500 hover:bg-slate-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                    {visibleNavItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                                    isActive
                                        ? 'bg-primary-50 text-primary-700 border border-primary-100 shadow-sm'
                                        : 'text-slate-700 hover:bg-slate-100'
                                }`
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <button
                    onClick={handleLogout}
                    className="m-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 shadow-sm"
                >
                    <LogOut className="h-4 w-4" />
                    Logout
                </button>
            </aside>

            <div className="flex-1 flex flex-col md:ml-0">
                <header className="md:hidden sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 ml-12">
                        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-white grid place-content-center shadow-sm">
                            <Layers className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="text-base font-semibold text-slate-900">User Management</div>
                            <div className="text-xs text-slate-500">
                                {location.pathname.replace('/', '') || 'dashboard'}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="px-3 py-2 rounded-md text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800"
                    >
                        Logout
                    </button>
                </header>
                <main className="p-4 md:p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default ShellLayout;
